import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
    Text,
    Avatar,
    FilledIconButton,
    EditIcon,
    HorizontalRule,
    NoInformation,
    Tag,
    Progress,
} from '../../index';

import { GET_STAFFS } from '../../../services/provider';
import { alertError } from '../../../redux/stateActions';

const ProviderList = ({ searchValue }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((state) => state.user);

    /*--------------- Provider List ---------------*/
    const [providerListData, setProviderListData] = useState([]);
    const { loading, error, data, refetch } = useQuery(GET_STAFFS, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        refetch();
    }, []);

    useEffect(() => {
        if (error) {
            dispatch(alertError(`Cannot Get Provider: ${error.message}`));
            return;
        }

        if (data && data.staffs) {
            const filtered = data.staffs.filter((s) => s.user !== null);

            if (!searchValue) {
                setProviderListData(filtered);
            } else {
                const newProviderListData = filtered.filter((provider) =>
                    provider?.user?.fullname.toLowerCase().includes(searchValue.toLowerCase())
                );
                setProviderListData(newProviderListData);
            }
        }
    }, [error, data, searchValue]);

    const handleEdit = (id) => {
        history.push({
            pathname: `/provider/consultant/${id}`,
            state: {
                prevPath: '/provider/consultant',
            },
        });
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                minHeight: '450px',
                position: 'relative',
            }}
        >
            {loading && <Progress />}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '48px 200px 150px 150px auto',
                    alignItems: 'center',
                    columnGap: '15px',
                }}
            >
                <div></div>
                <Text $bold>Name</Text>
                <Text $bold>Position</Text>
                <Text $bold>Role</Text>
                <Text $bold>Specialist</Text>
                <div></div>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {providerListData.length > 0 ? (
                    providerListData.map((provider, index) => (
                        <div
                            key={provider.id}
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                rowGap: '6px',
                                marginTop: index === 0 ? 0 : '6px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '48px 200px 150px 150px auto',
                                    alignItems: 'center',
                                    columnGap: '15px',
                                }}
                            >
                                <Avatar
                                    alt={`${provider?.user?.fullname}`}
                                    src={provider?.user?.avatar ? provider.user?.avatar?.url : null}
                                    $avatarSize="48px"
                                    deactivate={provider?.deactivate}
                                />
                                <Text
                                    style={{
                                        margin: '4px 0 8px',
                                        width: '200px',
                                    }}
                                    $ellipsis
                                    $bold
                                    $color={provider.deactivate ? 'gray5' : 'black'}
                                >{`${provider?.user?.fullname}`}</Text>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '150px',
                                    }}
                                >
                                    <Text
                                        $color={provider.deactivate ? 'gray5' : 'black'}
                                        $bold
                                        $lineClamp={1}
                                    >
                                        {provider.staff_position?.name}
                                    </Text>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '150px',
                                    }}
                                >
                                    <Text
                                        $color={provider.deactivate ? 'gray5' : 'black'}
                                        $bold
                                        $lineClamp={1}
                                    >
                                        {provider.staff_role?.name}
                                    </Text>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div style={{ display: 'flex', columnGap: '10px' }}>
                                        {provider.specialist?.slice(0, 2).map((tag, index) => (
                                            <Tag
                                                key={`specialist${index}`}
                                                deactivate={provider.deactivate}
                                            >
                                                {tag}
                                            </Tag>
                                        ))}
                                    </div>
                                    <div>
                                        <FilledIconButton
                                            $width="24px"
                                            $height="24px"
                                            $backgroundColor="purple"
                                            onClick={() => handleEdit(provider.id)}
                                        >
                                            <EditIcon />
                                        </FilledIconButton>
                                    </div>
                                </div>
                            </div>
                            <HorizontalRule />
                        </div>
                    ))
                ) : (
                    <NoInformation />
                )}
            </div>
        </div>
    );
};

ProviderList.propTypes = {
    searchValue: PropTypes.string,
};

export default ProviderList;
